/*
|--------------------------------------------------------------------------
| NUMBER / MONEY HELPERS
|--------------------------------------------------------------------------
*/

export const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

export const roundMoney = (
    value
) => {
    return (
        Math.round(
            (
                toNumber(
                    value
                ) +
                Number.EPSILON
            ) *
            100
        ) / 100
    );
};

export const formatCurrency = (
    value
) => {
    return new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );
};

/*
|--------------------------------------------------------------------------
| DATE HELPERS
|--------------------------------------------------------------------------
*/

export const formatDate = (
    value,
    includeTime = true
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",

                hour:
                    "2-digit",

                minute:
                    "2-digit",
            }
            : {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",
            }
    ).format(
        date
    );
};

/*
|--------------------------------------------------------------------------
| TEXT HELPERS
|--------------------------------------------------------------------------
*/

export const normalizeText = (
    value
) => {
    return String(
        value ||
        ""
    )
        .trim()
        .toLowerCase();
};

export const formatType = (
    value
) => {
    return String(
        value ||
        "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

/*
|--------------------------------------------------------------------------
| API RESPONSE NORMALIZER
|--------------------------------------------------------------------------
|
| Supports:
|
| response
| response.data
| response.data.data
| response.data.records
| response.data.data.records
| response.data.ledger
| response.data.entries
|--------------------------------------------------------------------------
*/

export const normalizeLedgerResponse = (
    response
) => {
    const root =
        response ||
        {};

    const level1 =
        root?.data ??
        root;

    const level2 =
        level1?.data ??
        level1;

    let records = [];

    if (
        Array.isArray(
            level2
        )
    ) {
        records =
            level2;
    } else if (
        Array.isArray(
            level2?.records
        )
    ) {
        records =
            level2.records;
    } else if (
        Array.isArray(
            level2?.ledger
        )
    ) {
        records =
            level2.ledger;
    } else if (
        Array.isArray(
            level2?.entries
        )
    ) {
        records =
            level2.entries;
    } else if (
        Array.isArray(
            level2?.items
        )
    ) {
        records =
            level2.items;
    } else if (
        Array.isArray(
            level1?.records
        )
    ) {
        records =
            level1.records;
    } else if (
        Array.isArray(
            level1?.ledger
        )
    ) {
        records =
            level1.ledger;
    } else if (
        Array.isArray(
            level1?.entries
        )
    ) {
        records =
            level1.entries;
    }

    const pagination =
        level2?.pagination ||
        level2?.meta ||
        level1?.pagination ||
        level1?.meta ||
        {};

    const summary =
        level2?.summary ||
        level1?.summary ||
        {};

    return {
        records,

        pagination,

        summary,
    };
};

/*
|--------------------------------------------------------------------------
| CUSTOMER HELPERS
|--------------------------------------------------------------------------
*/

export const getCustomerId = (
    row
) => {
    const customer =
        row?.customer;

    const paymentCustomer =
        row?.payment?.customer;

    const allocationCustomer =
        row?.paymentAllocation?.customer;

    /*
    |--------------------------------------------------------------------------
    | DIRECT CUSTOMER
    |--------------------------------------------------------------------------
    */

    if (
        customer &&
        typeof customer ===
        "object"
    ) {
        if (
            customer?._id
        ) {
            return customer._id;
        }

        if (
            customer?.id
        ) {
            return customer.id;
        }
    }

    if (
        typeof customer ===
        "string" &&
        customer.trim()
    ) {
        return customer;
    }

    /*
    |--------------------------------------------------------------------------
    | CUSTOMER ID
    |--------------------------------------------------------------------------
    */

    if (
        row?.customerId
    ) {
        return row.customerId;
    }

    /*
    |--------------------------------------------------------------------------
    | CUSTOMER SNAPSHOT
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    | Your working SaleInvoiceViewPage already uses both:
    |
    | customerSnapshot.customerId
    | customerSnapshot._id
    |--------------------------------------------------------------------------
    */

    if (
        row?.customerSnapshot
            ?.customerId
    ) {
        return row
            .customerSnapshot
            .customerId;
    }

    if (
        row?.customerSnapshot
            ?._id
    ) {
        return row
            .customerSnapshot
            ._id;
    }

    /*
    |--------------------------------------------------------------------------
    | PAYMENT CUSTOMER
    |--------------------------------------------------------------------------
    */

    if (
        paymentCustomer &&
        typeof paymentCustomer ===
        "object" &&
        paymentCustomer?._id
    ) {
        return paymentCustomer._id;
    }

    if (
        typeof paymentCustomer ===
        "string"
    ) {
        return paymentCustomer;
    }

    if (
        row?.payment
            ?.customerId
    ) {
        return row
            .payment
            .customerId;
    }

    /*
    |--------------------------------------------------------------------------
    | ALLOCATION CUSTOMER
    |--------------------------------------------------------------------------
    */

    if (
        allocationCustomer &&
        typeof allocationCustomer ===
        "object" &&
        allocationCustomer?._id
    ) {
        return allocationCustomer._id;
    }

    if (
        typeof allocationCustomer ===
        "string"
    ) {
        return allocationCustomer;
    }

    /*
    |--------------------------------------------------------------------------
    | DOCUMENT / INVOICE
    |--------------------------------------------------------------------------
    */

    const document =
        row?.document ||
        row?.invoice ||
        row?.saleInvoice;

    if (
        document?.customer?._id
    ) {
        return document
            .customer
            ._id;
    }

    if (
        typeof document
            ?.customer ===
        "string"
    ) {
        return document.customer;
    }

    if (
        document?.customerId
    ) {
        return document.customerId;
    }

    if (
        document
            ?.customerSnapshot
            ?.customerId
    ) {
        return document
            .customerSnapshot
            .customerId;
    }

    if (
        document
            ?.customerSnapshot
            ?._id
    ) {
        return document
            .customerSnapshot
            ._id;
    }

    /*
    |--------------------------------------------------------------------------
    | WEBSITE USER FALLBACK
    |--------------------------------------------------------------------------
    */

    return (
        row?.websiteUserId ||
        row?.userId ||
        ""
    );
};

export const getCustomerName = (
    row
) => {
    return (
        row?.customerSnapshot
            ?.customerName ||
        row?.customerSnapshot
            ?.name ||
        row?.customer
            ?.customerName ||
        row?.customer
            ?.name ||
        row?.customerName ||
        row?.name ||
        "Walk-in Customer"
    );
};

export const getCustomerMobile = (
    row
) => {
    return (
        row?.customerSnapshot
            ?.mobile ||
        row?.customer
            ?.mobile ||
        row?.customerMobile ||
        row?.mobile ||
        "-"
    );
};

export const getCustomerEmail = (
    row
) => {
    return (
        row?.customerSnapshot
            ?.email ||
        row?.customer
            ?.email ||
        row?.customerEmail ||
        row?.email ||
        ""
    );
};

/*
|--------------------------------------------------------------------------
| LEDGER FIELD HELPERS
|--------------------------------------------------------------------------
*/

export const getEntryType = (
    row
) => {
    return String(
        row?.entryType ||
        row?.transactionType ||
        row?.sourceType ||
        row?.type ||
        "ADJUSTMENT"
    ).toUpperCase();
};

export const getReference = (
    row
) => {
    return (
        row?.referenceNumber ||
        row?.documentNumber ||
        row?.invoiceNumber ||
        row?.receiptNumber ||
        row?.paymentNumber ||
        row?.creditNoteNumber ||
        row?.metadata
            ?.creditNoteNumber ||
        row?.metadata
            ?.saleInvoiceNumber ||
        row?.metadata
            ?.paymentNumber ||
        "-"
    );
};

export const getDescription = (
    row
) => {
    return (
        row?.narration ||
        row?.description ||
        row?.remarks ||
        row?.notes ||
        "-"
    );
};

export const getEntryDate = (
    row
) => {
    return (
        row?.transactionDate ||
        row?.entryDate ||
        row?.postingDate ||
        row?.documentDate ||
        row?.createdAt ||
        null
    );
};

/*
|--------------------------------------------------------------------------
| DEBIT
|--------------------------------------------------------------------------
*/

export const getDebit = (
    row
) => {
    const directDebit =
        row?.debit ??
        row?.debitAmount;

    if (
        directDebit !==
        undefined &&
        directDebit !==
        null
    ) {
        return roundMoney(
            directDebit
        );
    }

    const direction =
        String(
            row?.direction ||
            ""
        ).toUpperCase();

    if (
        direction ===
        "DEBIT"
    ) {
        return roundMoney(
            row?.amount
        );
    }

    return 0;
};

/*
|--------------------------------------------------------------------------
| CREDIT
|--------------------------------------------------------------------------
*/

export const getCredit = (
    row
) => {
    const directCredit =
        row?.credit ??
        row?.creditAmount;

    if (
        directCredit !==
        undefined &&
        directCredit !==
        null
    ) {
        return roundMoney(
            directCredit
        );
    }

    const direction =
        String(
            row?.direction ||
            ""
        ).toUpperCase();

    if (
        direction ===
        "CREDIT"
    ) {
        return roundMoney(
            row?.amount
        );
    }

    return 0;
};

/*
|--------------------------------------------------------------------------
| BALANCE
|--------------------------------------------------------------------------
*/

export const getBalance = (
    row
) => {
    return roundMoney(
        row?.runningBalance ??
        row?.balanceAmount ??
        row?.balance ??
        0
    );
};

/*
|--------------------------------------------------------------------------
| SEARCH HELPER
|--------------------------------------------------------------------------
*/

export const matchesLedgerSearch = (
    row,
    search
) => {
    const keyword =
        normalizeText(
            search
        );

    if (
        !keyword
    ) {
        return true;
    }

    const searchableText =
        [
            getCustomerName(
                row
            ),

            getCustomerMobile(
                row
            ),

            getCustomerEmail(
                row
            ),

            getReference(
                row
            ),

            getDescription(
                row
            ),

            getEntryType(
                row
            ),
        ]
            .filter(
                Boolean
            )
            .join(
                " "
            )
            .toLowerCase();

    return searchableText.includes(
        keyword
    );
};

/*
|--------------------------------------------------------------------------
| CALCULATE LEDGER SUMMARY
|--------------------------------------------------------------------------
*/

export const calculateLedgerSummary = (
    records = []
) => {
    const result =
        records.reduce(
            (
                summary,
                row
            ) => {
                summary.totalDebit =
                    roundMoney(
                        summary
                            .totalDebit +
                        getDebit(
                            row
                        )
                    );

                summary.totalCredit =
                    roundMoney(
                        summary
                            .totalCredit +
                        getCredit(
                            row
                        )
                    );

                return summary;
            },
            {
                totalDebit:
                    0,

                totalCredit:
                    0,
            }
        );

    return {
        ...result,

        netBalance:
            roundMoney(
                result.totalDebit -
                result.totalCredit
            ),
    };
};

/*
|--------------------------------------------------------------------------
| GROUP LEDGER BY CUSTOMER
|--------------------------------------------------------------------------
*/

export const groupLedgerByCustomer = (
    records = []
) => {
    const customerMap =
        new Map();

    records.forEach(
        (
            row
        ) => {
            const rawCustomerId =
                getCustomerId(
                    row
                );
            const customerId =
                typeof rawCustomerId ===
                    "object"
                    ? String(
                        rawCustomerId?._id ||
                        rawCustomerId?.id ||
                        ""
                    )
                    : String(
                        rawCustomerId ||
                        ""
                    );

            const name =
                getCustomerName(
                    row
                );

            const mobile =
                getCustomerMobile(
                    row
                );

            const email =
                getCustomerEmail(
                    row
                );

            /*
             * Actual ID is preferred.
             *
             * Mobile is fallback for legacy rows.
             * Name is last fallback.
             */
            const key =
                customerId ||
                (
                    mobile !== "-"
                        ? mobile
                        : ""
                ) ||
                name;

            if (
                !customerMap.has(
                    key
                )
            ) {
                customerMap.set(
                    key,
                    {
                        key,

                        customerId,

                        name,

                        mobile,

                        email,

                        totalDebit:
                            0,

                        totalCredit:
                            0,

                        balance:
                            0,

                        entryCount:
                            0,

                        lastActivity:
                            null,

                        entries:
                            [],
                    }
                );
            }

            const customer =
                customerMap.get(
                    key
                );

            customer.totalDebit =
                roundMoney(
                    customer
                        .totalDebit +
                    getDebit(
                        row
                    )
                );

            customer.totalCredit =
                roundMoney(
                    customer
                        .totalCredit +
                    getCredit(
                        row
                    )
                );

            customer.entryCount +=
                1;

            customer.entries.push(
                row
            );

            const entryDate =
                getEntryDate(
                    row
                );

            if (
                entryDate &&
                (
                    !customer
                        .lastActivity ||
                    new Date(
                        entryDate
                    ) >
                    new Date(
                        customer
                            .lastActivity
                    )
                )
            ) {
                customer.lastActivity =
                    entryDate;
            }
        }
    );

    return Array.from(
        customerMap.values()
    )
        .map(
            (
                customer
            ) => {
                const entries =
                    [
                        ...customer.entries,
                    ].sort(
                        (
                            first,
                            second
                        ) => {
                            return (
                                new Date(
                                    getEntryDate(
                                        second
                                    ) ||
                                    0
                                ) -
                                new Date(
                                    getEntryDate(
                                        first
                                    ) ||
                                    0
                                )
                            );
                        }
                    );

                const latestEntry =
                    entries[0];

                const latestBalance =
                    latestEntry
                        ? getBalance(
                            latestEntry
                        )
                        : 0;

                return {
                    ...customer,

                    entries,

                    balance:
                        latestBalance !==
                            0
                            ? latestBalance
                            : roundMoney(
                                customer
                                    .totalDebit -
                                customer
                                    .totalCredit
                            ),
                };
            }
        )
        .sort(
            (
                first,
                second
            ) => {
                return (
                    new Date(
                        second
                            .lastActivity ||
                        0
                    ) -
                    new Date(
                        first
                            .lastActivity ||
                        0
                    )
                );
            }
        );
};